import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Mail, MessageCircle, Plus, Save, X } from 'lucide-react';
import { getCommerceRule, saveCommerceRule } from '../../../../api/collections';
import { useCountry } from '../../../../context/CountryContext';
import { showToast } from '../../../../utils/showToast';
import { cn } from '../../../../utils/cn';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Switch } from '../../../../components/ui/Switch';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { FieldLabel } from '../../../../components/ui/FieldLabel';
import {
  buildRulePayload,
  emailToAddError,
  isContactEmailsRejection,
  normalizeEmail,
  ruleToForm,
  validateRuleForm,
  MAX_CONTACT_PHONE_LENGTH,
} from '../utils/commerceRuleForm';
import { apiErrorMessage, apiErrorStatus, collectionsErrorText } from '../utils/collectionReminders';

// De dónde sale cada destinatario efectivo (lo resuelve billing: regla ?? facturación ?? principal).
const RECIPIENT_SOURCE_LABELS = {
  commerce: 'Cargado en esta regla',
  billing: 'Contacto de facturación',
  primary: 'Contacto principal',
};

// Por qué el comercio NO recibiría WhatsApp.
const WHATSAPP_REASON_LABELS = {
  no_phone: 'Sin teléfono: no recibirá recordatorios por WhatsApp.',
  invalid_phone:
    'El teléfono no es un celular válido para WhatsApp (revisá el código de país): no recibirá recordatorios por WhatsApp.',
  no_country:
    'No se pudo determinar el país del operador: cargá el teléfono con código de país (por ejemplo +56912345678).',
  contacts_unavailable: 'No se pudieron leer los contactos del comercio. Reintentá en unos minutos.',
};

const Notice = ({ tone = 'warning', children }) => (
  <p
    className={cn(
      'flex items-start gap-1.5 rounded-md px-2.5 py-1.5 text-xs',
      tone === 'warning'
        ? 'bg-status-warning-soft text-status-warning'
        : 'bg-status-error-soft text-status-error',
    )}
  >
    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

/** A quién le llegaría HOY un recordatorio, según lo guardado (lo resuelve billing). */
function EffectiveRecipients({ recipients }) {
  const { t } = useTranslation();
  if (!recipients) return null;
  const { emails = [], emailSource, phone, phoneSource, whatsappEligible, reason, contactsLookup } =
    recipients;

  return (
    <section
      className="rounded-lg border border-border bg-bg-muted/40 p-4 space-y-3"
      aria-label={t('Destinatarios de los recordatorios')}
    >
      <div>
        <h4 className="text-sm font-semibold text-fg">{t('Destinatarios de los recordatorios')}</h4>
        <p className="text-xs text-fg-muted">
          {t('A quién le llegaría hoy un recordatorio, según lo guardado.')}
        </p>
      </div>

      {contactsLookup === 'unavailable' && (
        <Notice>
          {t('No se pudieron leer los contactos de facturación del comercio: lo que se muestra puede estar incompleto.')}
        </Notice>
      )}
      {contactsLookup === 'not_configured' && (
        <Notice>
          {t('Este entorno no tiene configurada la lectura de contactos del comercio: solo se muestran los de esta regla.')}
        </Notice>
      )}

      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 text-sm font-medium text-fg">
          <Mail className="h-4 w-4 text-fg-muted" aria-hidden />
          {t('Correo')}
        </div>
        {emails.length > 0 ? (
          <>
            <ul className="flex flex-wrap gap-1.5">
              {emails.map((email) => (
                <li key={email} className="rounded-full bg-bg px-2 py-0.5 text-xs text-fg ring-1 ring-inset ring-border">
                  {email}
                </li>
              ))}
            </ul>
            {RECIPIENT_SOURCE_LABELS[emailSource] && (
              <p className="text-xs text-fg-muted">{t(RECIPIENT_SOURCE_LABELS[emailSource])}</p>
            )}
          </>
        ) : (
          <Notice>{t('Sin correo: no recibirá recordatorios por correo.')}</Notice>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 text-sm font-medium text-fg">
          <MessageCircle className="h-4 w-4 text-fg-muted" aria-hidden />
          {t('WhatsApp')}
        </div>
        {phone && (
          <p className="flex flex-wrap items-center gap-2 text-sm text-fg">
            <span className="font-mono">{phone}</span>
            {whatsappEligible && (
              <span className="inline-flex items-center gap-1 text-xs text-status-success">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                {t('Recibirá WhatsApp')}
              </span>
            )}
          </p>
        )}
        {phone && RECIPIENT_SOURCE_LABELS[phoneSource] && (
          <p className="text-xs text-fg-muted">{t(RECIPIENT_SOURCE_LABELS[phoneSource])}</p>
        )}
        {!whatsappEligible && (
          <Notice>
            {t(WHATSAPP_REASON_LABELS[reason] ?? 'No recibirá recordatorios por WhatsApp.')}
          </Notice>
        )}
      </div>
    </section>
  );
}

/**
 * Regla de cobranza de UN comercio: plazo propio o heredado, pausa de los recordatorios
 * automáticos, correos y teléfono de cobranza, y a quién le llegarían hoy los recordatorios.
 *
 * Se usa en Empresas → Cobranza y en un Drawer desde /admin/cobro (morosos y detalle de
 * nota). Solo usa componentes ui + Tailwind: se monta fuera de la vista de cobro, donde las
 * clases bill-* no están cargadas.
 *
 * El teléfono viaja tal como se escribe: billing lo normaliza a E.164 con el país del
 * operador (es la única normalización, la misma que usa el envío) y devuelve el resultado.
 *
 * @param {{ companyId: string, onSaved?: (view: object) => void }} props
 */
export default function CommerceCollectionRuleForm({ companyId, onSaved }) {
  const { t } = useTranslation();
  const baseId = useId();
  const { countryName, resolved: countryResolved } = useCountry();

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const applyView = useCallback((data) => {
    const next = ruleToForm(data?.rule);
    setView(data);
    setInitial(next);
    setForm(next);
    setErrors({});
    setSaveError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await getCommerceRule(companyId);
      applyView(data);
    } catch (err) {
      setLoadError({ status: apiErrorStatus(err), message: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [companyId, applyView]);

  useEffect(() => {
    if (!companyId) return;
    setView(null);
    setForm(null);
    setEmailDraft('');
    setEmailError(null);
    load();
  }, [companyId, load]);

  const payload = useMemo(
    () => (form && initial ? buildRulePayload(form, initial) : {}),
    [form, initial],
  );
  const dirty = Object.keys(payload).length > 0 || emailDraft.trim() !== '';

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaveError(null);
  };

  // Agrega el correo escrito. Devuelve la lista resultante o null si no se pudo.
  const addDraftEmail = () => {
    const email = normalizeEmail(emailDraft);
    const problem = emailToAddError(email, form.contactEmails);
    if (problem) {
      setEmailError(problem);
      return null;
    }
    const next = [...form.contactEmails, email];
    update({ contactEmails: next });
    setEmailDraft('');
    setEmailError(null);
    return next;
  };

  const removeEmail = (email) =>
    update({ contactEmails: form.contactEmails.filter((e) => e !== email) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Un correo escrito y sin "Agregar" se perdería en silencio al guardar: se agrega (o se
    // frena el guardado si no es válido).
    let effectiveForm = form;
    if (emailDraft.trim() !== '') {
      const emails = addDraftEmail();
      if (!emails) return;
      effectiveForm = { ...form, contactEmails: emails };
    }
    const validation = validateRuleForm(effectiveForm);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const body = buildRulePayload(effectiveForm, initial);
    if (Object.keys(body).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await saveCommerceRule(companyId, body);
      applyView(data);
      showToast(t('Regla de cobranza guardada.'), 'success');
      onSaved?.(data);
    } catch (err) {
      const text = isContactEmailsRejection(err)
        ? t('Algún correo de cobranza no es válido. Revisá la lista y volvé a guardar.')
        : collectionsErrorText(err, t, 'No se pudo guardar la regla de cobranza.');
      setSaveError(text);
      showToast(text, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !view) {
    return (
      <div className="space-y-3" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (loadError && !view) {
    const text =
      loadError.status === 403
        ? t('Tu rol no tiene permiso para gestionar la cobranza.')
        : loadError.status === 404
          ? t('Este comercio no tiene contrato ni notas de cobro con tu operador.')
          : loadError.message || t('No se pudo cargar la regla de cobranza del comercio.');
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
        <span>{text}</span>
        {loadError.status !== 403 && loadError.status !== 404 && (
          <Button size="sm" variant="outline" onClick={load}>
            {t('Reintentar')}
          </Button>
        )}
      </div>
    );
  }

  if (!form) return null;

  const effective = view?.effective ?? {};
  const inheritsNow = !initial.ownTerm;
  const inheritHint = inheritsNow
    ? Number(effective.paymentTermDays) > 0
      ? t('Hoy: {{n}} días (plazo general del operador).', { n: effective.paymentTermDays })
      : t('El operador no tiene plazo general: sus notas no vencen.')
    : null;
  const ids = {
    term: `${baseId}-term`,
    paused: `${baseId}-paused`,
    email: `${baseId}-email`,
    phone: `${baseId}-phone`,
  };

  return (
    <div className="space-y-5">
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        {/* Plazo de vencimiento */}
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-fg">{t('Plazo de vencimiento')}</legend>
          <label className="flex items-start gap-2 text-sm text-fg cursor-pointer">
            <input
              type="radio"
              name={`${baseId}-term-mode`}
              className="mt-1 accent-brand-500"
              checked={!form.ownTerm}
              onChange={() => update({ ownTerm: false })}
            />
            <span>
              {t('Hereda el plazo general del operador')}
              {inheritHint && <span className="block text-xs text-fg-muted">{inheritHint}</span>}
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-fg cursor-pointer">
            <input
              type="radio"
              name={`${baseId}-term-mode`}
              className="mt-1 accent-brand-500"
              checked={form.ownTerm}
              onChange={() => update({ ownTerm: true })}
            />
            <span>{t('Plazo propio para este comercio')}</span>
          </label>
          {form.ownTerm && (
            <div className="ml-6 flex items-center gap-2">
              <Input
                id={ids.term}
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                className="w-28"
                value={form.termDays}
                invalid={!!errors.termDays}
                aria-label={t('Días de plazo')}
                aria-describedby={errors.termDays ? `${ids.term}-error` : undefined}
                onChange={(e) => update({ termDays: e.target.value })}
              />
              <span className="text-sm text-fg-muted">{t('días corridos desde la emisión')}</span>
            </div>
          )}
          {errors.termDays && (
            <p id={`${ids.term}-error`} className="ml-6 text-xs text-status-error">
              {t(errors.termDays)}
            </p>
          )}
          <p className="text-xs text-fg-muted">
            {t('Aplica a las notas que se emitan desde ahora; las ya emitidas conservan su vencimiento.')}
          </p>
        </fieldset>

        {/* Pausa de automáticos */}
        <div className="space-y-1">
          <Switch
            id={ids.paused}
            checked={form.remindersPaused}
            onChange={(v) => update({ remindersPaused: v })}
            label={t('Pausar los recordatorios automáticos')}
          />
          <p className="text-xs text-fg-muted">
            {t('Los envíos programados y el "Enviar recordatorio" manual siguen disponibles.')}
          </p>
        </div>

        {/* Correos de cobranza */}
        <div className="space-y-2">
          <FieldLabel htmlFor={ids.email}>{t('Correos de cobranza')}</FieldLabel>
          {form.contactEmails.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label={t('Correos cargados')}>
              {form.contactEmails.map((email) => (
                <li
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pl-2.5 pr-1 text-xs text-brand-700"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    aria-label={t('Quitar {{email}}', { email })}
                    className="rounded-full p-0.5 hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <Input
              id={ids.email}
              type="email"
              autoComplete="off"
              placeholder="pagos@comercio.cl"
              value={emailDraft}
              invalid={!!emailError}
              aria-describedby={emailError ? `${ids.email}-error` : `${ids.email}-hint`}
              onChange={(e) => {
                setEmailDraft(e.target.value);
                setEmailError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDraftEmail();
                }
              }}
            />
            <Button
              variant="outline"
              onClick={addDraftEmail}
              iconLeft={<Plus className="h-4 w-4" />}
              disabled={!emailDraft.trim()}
            >
              {t('Agregar')}
            </Button>
          </div>
          {emailError ? (
            <p id={`${ids.email}-error`} className="text-xs text-status-error">
              {t(emailError)}
            </p>
          ) : (
            <p id={`${ids.email}-hint`} className="text-xs text-fg-muted">
              {t('Si no cargás ninguno se usan los del contacto de facturación o, si no hay, los del contacto principal.')}
            </p>
          )}
        </div>

        {/* Teléfono de cobranza */}
        <div className="space-y-2">
          <FieldLabel htmlFor={ids.phone}>{t('Teléfono de cobranza (WhatsApp)')}</FieldLabel>
          <Input
            id={ids.phone}
            type="tel"
            inputMode="tel"
            autoComplete="off"
            maxLength={MAX_CONTACT_PHONE_LENGTH}
            placeholder="+56 9 1234 5678"
            value={form.contactPhone}
            invalid={!!errors.contactPhone}
            aria-describedby={`${ids.phone}-hint`}
            onChange={(e) => update({ contactPhone: e.target.value })}
          />
          <p id={`${ids.phone}-hint`} className={cn('text-xs', errors.contactPhone ? 'text-status-error' : 'text-fg-muted')}>
            {errors.contactPhone
              ? t(errors.contactPhone)
              : countryResolved && countryName
                ? t('Sin código de país se toma como un número de {{pais}}. Vacío: se usa el de facturación o el principal.', {
                    pais: countryName,
                  })
                : t('Sin código de país se toma con el país del operador. Vacío: se usa el de facturación o el principal.')}
          </p>
        </div>

        {saveError && (
          <p className="rounded-md bg-status-error-soft px-3 py-2 text-sm text-status-error" role="alert">
            {saveError}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setForm(initial);
              setEmailDraft('');
              setEmailError(null);
              setErrors({});
              setSaveError(null);
            }}
            disabled={!dirty || saving}
          >
            {t('Descartar cambios')}
          </Button>
          <Button type="submit" loading={saving} disabled={!dirty} iconLeft={<Save className="h-4 w-4" />}>
            {t('Guardar')}
          </Button>
        </div>
      </form>

      <EffectiveRecipients recipients={view?.effectiveRecipients} />
    </div>
  );
}
